import React, { createContext, useCallback, useContext, useState } from 'react';
import { QuickReportsExportPanelProps } from '../../../DataTableAdvancedElements/DataTableQuickReports/QuickReportsExportPanel/QuickReportsExportPanel';
import { useDeepMemo } from '../../../../../hooks/useCompare';
import { DataTableErrorProps } from '../../../DataTableAdvancedElements/DataTableError';

type ForwarderPropsMap = {
  quickReports: QuickReportsExportPanelProps<any, any>;
  error: DataTableErrorProps;
};

type ForwarderKeys = 'quickReports' | 'error';

type ForwarderMap = Map<ForwarderKeys, QuickReportsExportPanelProps<any, any> | DataTableErrorProps>;

const fn = () => {};
const booleanFn = () => false;
const undefinedFn = () => undefined;

const ForwarderContext = createContext<ForwarderProviderProps>({
  setForwarderProps: fn,
  isEnabled: booleanFn,
  getProps: undefinedFn
});

interface ForwarderProviderProps {
  setForwarderProps: (key: ForwarderKeys, props: ForwarderPropsMap[ForwarderKeys]) => void;
  getProps: <K extends ForwarderKeys>(key: K) => ForwarderPropsMap[K] | undefined;
  isEnabled: (key: keyof ForwarderPropsMap) => boolean;
}

export function PropsForwarderProvider({ children }: React.PropsWithChildren) {
  const [props, setProps] = useState<ForwarderMap>(new Map());

  const setForwarderProps = useCallback(
    (key: ForwarderKeys, props: ForwarderPropsMap[ForwarderKeys]) => setProps((prevState) => prevState.set(key, props)),
    []
  );

  const getProps = useCallback(
    <K extends ForwarderKeys>(key: K) => props.get(key) as ForwarderPropsMap[K] | undefined,
    [props]
  );

  const isEnabled = useCallback((key: ForwarderKeys) => props.has(key), [props]);

  const value = useDeepMemo(
    () => ({ setForwarderProps, getProps, isEnabled }),
    [setForwarderProps, getProps, isEnabled]
  );

  return <ForwarderContext.Provider value={value}>{children}</ForwarderContext.Provider>;
}

export function usePropsForwarderContext() {
  return useContext(ForwarderContext);
}
